// ─── ReviewMode.js ────────────────────────────────────────────────────────────
// Desktop master-detail review dashboard.
// Left: area navigation. Right: per-substrate adjusted hours table.
// Traffic light system: green (≤15%), amber (15–30%), red (>30%).

import React, { useState, useRef, useEffect } from 'react';
import { calcAreaHours, calcSubstrateBreakdown } from './calcUtils';
import { METHOD_CONFIG } from './areaConstants';
import { REVIEW_THRESHOLDS } from './dataModel';
import { ACCESS_OPTIONS } from './AccessTags';
import ConfirmModal from './ConfirmModal';
import './ReviewMode.css';

// ── Traffic light logic ──────────────────────────────────────────────────────
function getTrafficLight(measured, assessed) {
  if (!measured || !assessed || measured === 0) return { color: 'grey', icon: '⚪', label: 'N/A' };
  const pct = Math.abs((assessed - measured) / measured) * 100;
  const sign = assessed > measured ? '+' : assessed < measured ? '−' : '';
  if (pct <= REVIEW_THRESHOLDS.green) return { color: 'green', icon: '🟢', label: `${sign}${pct.toFixed(0)}%` };
  if (pct <= REVIEW_THRESHOLDS.amber) return { color: 'amber', icon: '🟡', label: `${sign}${pct.toFixed(0)}%` };
  return { color: 'red', icon: '🔴', label: `${sign}${pct.toFixed(0)}%` };
}

function accessLabel(id) {
  const all = ACCESS_OPTIONS || [];
  const opt = all.find(o => o.id === id);
  return opt ? `${opt.icon} ${opt.label}` : id;
}

// Get all substrates for an area (measured + PM-added)
function getAreaSubstrates(area) {
  const breakdown = calcSubstrateBreakdown(area);
  const pmAdded = area.pmAddedSurfaces || [];
  const measuredSubs = Object.keys(breakdown);
  return [...measuredSubs, ...pmAdded.filter(s => !measuredSubs.includes(s))];
}

// Compute area-level totals
function getAreaTotals(area, adjustedBySubstrate) {
  const breakdown = calcSubstrateBreakdown(area);
  const assessData = area.assessBySubstrate || {};
  const subs = getAreaSubstrates(area);
  const measCalc = calcAreaHours(area);

  let totalAssessed = 0, totalAdjusted = 0;
  subs.forEach(sub => {
    totalAssessed += parseFloat(assessData[sub]?.hours) || 0;
    totalAdjusted += parseFloat(adjustedBySubstrate[sub]) || 0;
  });

  return {
    measured: measCalc.total,
    assessed: totalAssessed,
    adjusted: totalAdjusted,
    breakdown,
    subs,
    light: getTrafficLight(measCalc.total, totalAssessed),
  };
}

// ── Area nav item (left panel) ──────────────────────────────────────────────
function AreaNavItem({ area, totals, isSelected, onClick, isComplete }) {
  const light = totals.light;
  return (
    <button
      className={`rv2-nav-item ${isSelected ? 'rv2-nav-selected' : ''} rv2-nav-${light.color}`}
      onClick={onClick}
    >
      <span className="rv2-nav-icon">{area.type === 'internal' ? '🏠' : '🏗️'}</span>
      <div className="rv2-nav-info">
        <div className="rv2-nav-name">{area.name || 'Unnamed'}</div>
        <div className="rv2-nav-hours">
          {totals.measured.toFixed(1)}h → {totals.assessed > 0 ? `${totals.assessed.toFixed(1)}h` : '—'} → {totals.adjusted > 0 ? `${totals.adjusted.toFixed(1)}h` : '—'}
        </div>
      </div>
      <div className="rv2-nav-right">
        <span className={`rv2-nav-light rv2-light-${light.color}`}>{light.icon}</span>
        {isComplete && <span className="rv2-nav-check">✓</span>}
      </div>
    </button>
  );
}

// ── Main ReviewMode ─────────────────────────────────────────────────────────
export default function ReviewMode({ project, onAmendArea, onSaveApproved, onBack, readOnly }) {
  const areas = project.areas || [];
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const dirty = useRef(false);

  // State: per-area, per-substrate adjusted hours
  // Shape: { [areaFirestoreId]: { [substrate]: hourString } }
  const [adjustedMap, setAdjustedMap] = useState(() => {
    const init = {};
    areas.forEach(a => {
      const assessData = a.assessBySubstrate || {};
      const existing = a.approvedBySubstrate || {};
      const subs = getAreaSubstrates(a);
      const areaAdj = {};
      subs.forEach(sub => {
        // Priority: existing approved > assessed > blank
        if (existing[sub]?.hours != null && existing[sub]?.hours !== '' && existing[sub]?.hours !== 0) {
          areaAdj[sub] = String(existing[sub].hours);
        } else if (assessData[sub]?.hours != null && parseFloat(assessData[sub].hours) > 0) {
          areaAdj[sub] = String(parseFloat(assessData[sub].hours).toFixed(1));
        } else {
          areaAdj[sub] = '';
        }
      });
      init[a.firestoreId] = areaAdj;
    });
    return init;
  });

  // beforeunload guard
  useEffect(() => {
    const handler = (e) => { if (dirty.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const updateSubstrateHours = (areaId, substrate, value) => {
    dirty.current = true;
    setAdjustedMap(prev => ({
      ...prev,
      [areaId]: { ...prev[areaId], [substrate]: value },
    }));
  };

  const resetAreaTo = (areaId, source) => {
    const area = areas.find(a => a.firestoreId === areaId);
    if (!area) return;
    dirty.current = true;
    const breakdown = calcSubstrateBreakdown(area);
    const assessData = area.assessBySubstrate || {};
    const subs = getAreaSubstrates(area);
    const newAdj = {};
    subs.forEach(sub => {
      if (source === 'measured') {
        const mh = breakdown[sub]?.hours || 0;
        newAdj[sub] = mh > 0 ? mh.toFixed(1) : '';
      } else {
        const ah = parseFloat(assessData[sub]?.hours) || 0;
        newAdj[sub] = ah > 0 ? ah.toFixed(1) : '';
      }
    });
    setAdjustedMap(prev => ({ ...prev, [areaId]: newAdj }));
  };

  // ── Compute all area totals ──────────────────────────────────────────────
  let grandMeasured = 0, grandAssessed = 0, grandAdjusted = 0;
  const areaTotals = areas.map(area => {
    const adj = adjustedMap[area.firestoreId] || {};
    const totals = getAreaTotals(area, adj);
    grandMeasured += totals.measured;
    grandAssessed += totals.assessed;
    grandAdjusted += totals.adjusted;
    return totals;
  });

  const counts = { green: 0, amber: 0, red: 0, grey: 0 };
  areaTotals.forEach(t => { counts[t.light.color] = (counts[t.light.color] || 0) + 1; });

  const areasComplete = areas.filter((a, i) => areaTotals[i].adjusted > 0).length;
  const allComplete = areasComplete === areas.length;

  // ── Selected area ────────────────────────────────────────────────────────
  const selectedArea = areas[selectedIdx] || areas[0];
  const selectedId = selectedArea?.firestoreId;
  const selectedTotals = areaTotals[selectedIdx] || areaTotals[0];
  const selectedAdj = adjustedMap[selectedId] || {};
  const assessData = selectedArea?.assessBySubstrate || {};
  const breakdown = selectedTotals?.breakdown || {};
  const pmAdded = selectedArea?.pmAddedSurfaces || [];
  const accessNeeds = selectedArea?.accessNeeds || [];

  // ── Save ──────────────────────────────────────────────────────────────────
  const buildSavePayload = () => {
    const payload = {};
    areas.forEach(a => {
      const adj = adjustedMap[a.firestoreId] || {};
      const bySubstrate = {};
      let total = 0;
      Object.entries(adj).forEach(([sub, val]) => {
        const h = parseFloat(val) || 0;
        bySubstrate[sub] = { hours: h };
        total += h;
      });
      payload[a.firestoreId] = {
        approvedBySubstrate: bySubstrate,
        approvedHours: total > 0 ? total.toFixed(1) : null,
      };
    });
    return payload;
  };

  const handleSave = async () => {
    await onSaveApproved(buildSavePayload());
    dirty.current = false;
  };

  const handleFinalise = () => {
    if (!allComplete) {
      const missing = areas.filter((a, i) => areaTotals[i].adjusted <= 0).map(a => a.name || 'Unnamed').join(', ');
      setConfirm({
        title: 'Missing adjusted hours',
        message: `These areas don't have adjusted hours yet:\n\n${missing}\n\nPlease enter adjusted hours for all areas before finalising.`,
        confirmLabel: 'OK',
        onConfirm: () => setConfirm(null),
      });
      return;
    }
    setConfirm({
      title: 'Finalise adjusted hours',
      message: `Finalise this project with ${grandAdjusted.toFixed(1)} total adjusted hours across ${areas.length} areas?\n\nYou can still make changes afterwards.`,
      confirmLabel: 'Finalise',
      onConfirm: async () => {
        setConfirm(null);
        await onSaveApproved(buildSavePayload(), true);
      },
    });
  };

  const handleBack = async () => {
    if (dirty.current) await handleSave();
    onBack();
  };

  if (!selectedArea) return null;

  return (
    <div className="rv2-screen">
      {confirm && (
        <ConfirmModal title={confirm.title} message={confirm.message}
          confirmLabel={confirm.confirmLabel} danger={confirm.danger}
          onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="s2s-header">
        <button className="s2s-back-btn" onClick={handleBack}>← Back</button>
        <div className="s2s-header-center">
          <div className="s2s-header-title">⚖️ Compare Hours {readOnly ? '(View Only)' : ''}</div>
          <div className="s2s-header-sub">{project.projectName || project.clientName || 'Project'}</div>
        </div>
        <div style={{ width: 60 }} />
      </div>

      {/* ── Summary strip ──────────────────────────────────────────────────── */}
      <div className="rv2-summary-strip">
        <div className="rv2-summary-stats">
          <span className="rv2-stat">📏 Measured: <strong>{grandMeasured.toFixed(1)}h</strong></span>
          <span className="rv2-stat rv2-stat-assess">📋 Assessed: <strong>{grandAssessed.toFixed(1)}h</strong></span>
          <span className="rv2-stat rv2-stat-adjusted">✅ Adjusted: <strong>{grandAdjusted > 0 ? `${grandAdjusted.toFixed(1)}h` : '—'}</strong></span>
        </div>
        <div className="rv2-summary-lights">
          {counts.green > 0 && <span className="rv2-badge rv2-badge-green">🟢 {counts.green}</span>}
          {counts.amber > 0 && <span className="rv2-badge rv2-badge-amber">🟡 {counts.amber}</span>}
          {counts.red > 0 && <span className="rv2-badge rv2-badge-red">🔴 {counts.red}</span>}
          <span className="rv2-progress-text">{areasComplete}/{areas.length} areas</span>
        </div>
      </div>

      {/* ── Master-detail body ─────────────────────────────────────────────── */}
      <div className="rv2-body">

        {/* ── Left: Area nav ──────────────────────────────────────────────── */}
        <div className="rv2-nav">
          <div className="rv2-nav-label">🏠 Internal</div>
          {areas.map((a, i) => a.type === 'internal' ? (
            <AreaNavItem key={a.firestoreId} area={a} totals={areaTotals[i]}
              isSelected={i === selectedIdx} isComplete={areaTotals[i].adjusted > 0}
              onClick={() => setSelectedIdx(i)} />
          ) : null)}
          <div className="rv2-nav-label" style={{marginTop: 12}}>🏗️ External</div>
          {areas.map((a, i) => a.type === 'external' ? (
            <AreaNavItem key={a.firestoreId} area={a} totals={areaTotals[i]}
              isSelected={i === selectedIdx} isComplete={areaTotals[i].adjusted > 0}
              onClick={() => setSelectedIdx(i)} />
          ) : null)}
        </div>

        {/* ── Right: Substrate detail ─────────────────────────────────────── */}
        <div className="rv2-detail">
          {/* Area header */}
          <div className="rv2-area-header">
            <div className="rv2-area-title">
              <span className="rv2-area-type-icon">{selectedArea.type === 'internal' ? '🏠' : '🏗️'}</span>
              <div>
                <h2 className="rv2-area-name">{selectedArea.name || 'Unnamed'}</h2>
                {selectedArea.tag && <span className="rv2-area-tag">{selectedArea.tag}</span>}
              </div>
            </div>
            {!readOnly && (
              <div className="rv2-area-actions">
                <button className="rv2-reset-btn" onClick={() => resetAreaTo(selectedId, 'measured')} title="Reset all to measured hours">📏 Use All Measured</button>
                <button className="rv2-reset-btn" onClick={() => resetAreaTo(selectedId, 'assessed')} title="Reset all to assessed hours">📋 Use All Assessed</button>
              </div>
            )}
          </div>

          {/* Substrate table */}
          <div className="rv2-table-wrap">
            <table className="rv2-table">
              <thead>
                <tr>
                  <th>Surface</th>
                  <th className="rv2-num">m²</th>
                  <th className="rv2-num">Measured</th>
                  <th className="rv2-num">Assessed</th>
                  <th className="rv2-num">Δ</th>
                  <th className="rv2-adj-col">Adjusted Hours</th>
                  <th>PM Note</th>
                </tr>
              </thead>
              <tbody>
                {(selectedTotals?.subs || []).map(sub => {
                  const bd = breakdown[sub];
                  const mh = bd ? bd.hours : 0;
                  const ah = parseFloat(assessData[sub]?.hours) || 0;
                  const adjVal = selectedAdj[sub] || '';
                  const light = getTrafficLight(mh, ah);
                  const isPM = pmAdded.includes(sub);
                  const note = assessData[sub]?.note || '';
                  const adjNum = parseFloat(adjVal) || 0;
                  const matchesMeasured = adjNum > 0 && Math.abs(adjNum - mh) < 0.05;
                  const matchesAssessed = adjNum > 0 && Math.abs(adjNum - ah) < 0.05;

                  return (
                    <tr key={sub} className={isPM ? 'rv2-pm-row' : ''}>
                      <td className="rv2-surface-name">
                        {sub}
                        {isPM && <span className="rv2-pm-tag">PM added</span>}
                        {(() => { const m = selectedArea?.surfaceSettings?.[sub]?.method; return m && m !== 'brush_roll' ? <span className={`rv2-method-tag rv2-method-${m}`}>{METHOD_CONFIG[m]?.icon} {METHOD_CONFIG[m]?.label}</span> : null; })()}
                      </td>
                      <td className="rv2-num">{bd ? bd.m2.toFixed(1) : '—'}</td>
                      <td className="rv2-num">{mh > 0 ? mh.toFixed(1) : '—'}</td>
                      <td className="rv2-num rv2-assessed-val">{ah > 0 ? ah.toFixed(1) : '—'}</td>
                      <td className={`rv2-num rv2-delta rv2-delta-${light.color}`}>{light.icon} {light.label}</td>
                      <td className="rv2-adj-cell">
                        {readOnly ? (
                          <span className="rv2-adj-readonly">{adjVal || '—'}</span>
                        ) : (
                          <div className="rv2-adj-group">
                            <button
                              className={`rv2-pick-btn rv2-pick-meas ${matchesMeasured ? 'rv2-pick-active' : ''}`}
                              onClick={() => updateSubstrateHours(selectedId, sub, mh > 0 ? mh.toFixed(1) : '')}
                              title="Use measured hours"
                              disabled={mh <= 0}
                            >📏 {mh > 0 ? mh.toFixed(1) : '—'}</button>
                            <button
                              className={`rv2-pick-btn rv2-pick-assess ${matchesAssessed ? 'rv2-pick-active' : ''}`}
                              onClick={() => updateSubstrateHours(selectedId, sub, ah > 0 ? ah.toFixed(1) : '')}
                              title="Use assessed hours"
                              disabled={ah <= 0}
                            >📋 {ah > 0 ? ah.toFixed(1) : '—'}</button>
                            <input
                              className="rv2-adj-input"
                              type="text"
                              inputMode="decimal"
                              placeholder="—"
                              value={adjVal}
                              onChange={e => updateSubstrateHours(selectedId, sub, e.target.value)}
                            />
                          </div>
                        )}
                      </td>
                      <td className="rv2-note-cell">{note && <span className="rv2-note-text" title={note}>💬 {note}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="rv2-total-row">
                  <td><strong>Area Total</strong></td>
                  <td className="rv2-num"></td>
                  <td className="rv2-num"><strong>{selectedTotals.measured.toFixed(1)}h</strong></td>
                  <td className="rv2-num rv2-assessed-val"><strong>{selectedTotals.assessed > 0 ? `${selectedTotals.assessed.toFixed(1)}h` : '—'}</strong></td>
                  <td className={`rv2-num rv2-delta rv2-delta-${selectedTotals.light.color}`}><strong>{selectedTotals.light.icon} {selectedTotals.light.label}</strong></td>
                  <td className="rv2-adj-cell"><strong className="rv2-total-adjusted">{selectedTotals.adjusted > 0 ? `${selectedTotals.adjusted.toFixed(1)}h` : '—'}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Area context: notes, access, photos */}
          <div className="rv2-context">
            {selectedArea.assessNotes && (
              <div className="rv2-context-block rv2-pm-notes">
                <span className="rv2-context-label">📝 PM Notes</span>
                <span>{selectedArea.assessNotes}</span>
              </div>
            )}
            {selectedArea.scopeNotes && (
              <div className="rv2-context-block rv2-bdm-notes">
                <span className="rv2-context-label">💬 BDM Scope Notes</span>
                <span>{selectedArea.scopeNotes}</span>
              </div>
            )}
            {accessNeeds.length > 0 && (
              <div className="rv2-context-block">
                <span className="rv2-context-label">🦺 Access Needs</span>
                <div className="rv2-access-tags">
                  {accessNeeds.map(id => <span key={id} className="rv2-access-tag">{accessLabel(id)}</span>)}
                </div>
              </div>
            )}
            {(selectedArea.photos || []).length > 0 && (
              <div className="rv2-context-block">
                <span className="rv2-context-label">📷 Photos</span>
                <div className="rv2-photos">
                  {selectedArea.photos.map(p => (
                    <img key={p.id} src={p.thumbnailUrl || p.url} alt="" className="rv2-photo" />
                  ))}
                </div>
              </div>
            )}
            {!readOnly && (
              <button className="rv2-amend-btn" onClick={() => onAmendArea(selectedArea)}>
                ✏️ Amend Measurements
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer actions ─────────────────────────────────────────────────── */}
      {!readOnly && (
        <div className="rv2-footer">
          <button className="rv2-btn rv2-btn-save" onClick={handleSave}>💾 Save Progress</button>
          <button className={`rv2-btn rv2-btn-finalise ${allComplete ? 'rv2-ready' : ''}`} onClick={handleFinalise}>
            ✅ Finalise Hours ({grandAdjusted > 0 ? `${grandAdjusted.toFixed(1)}h` : '—'})
          </button>
        </div>
      )}
    </div>
  );
}
